import { api } from "./client";
import type { ApiResponse } from "./types";

// ============================================================================
// Circles — friend groups. The other half of the retention unit.
//
// A household is who you LIVE with; a circle is who you GO OUT with, and the
// difference is why circles are plural and a household is not. You get one
// household and up to ten circles, and `circle_members` is unique on the PAIR
// rather than on the user — that uniqueness is the whole reason a person can
// be in the Sunday cricket lot and the garba group at the same time.
//
// Invites are pull-never-force with silent declines, same as households: an
// invitation you ignore does not tell the sender you ignored it. The invite
// LINK has no recipient column at all, deliberately — it is a WhatsApp-
// shareable token, revocable and expiring, and storing who it was meant for
// would turn a shareable link into a record of who was asked.
// ============================================================================

export type CircleRole = "owner" | "admin" | "member";

export interface CircleMember {
  userId: string;
  role: CircleRole;
  joinedAt: string;
  user: { id: string; name: string; avatarUrl: string | null };
}

export interface Circle {
  id: string;
  name: string;
  emoji: string | null;
  createdAt: string;
  /** Who started it. On the detail and on every write's return. */
  createdBy?: { id: string; name: string; avatarUrl: string | null; city: string | null };
  memberCount: number;
  myRole: CircleRole | null;
  members?: CircleMember[];
}

/** GET /circles/{id}/events: the coming events members of the circle are going to. */
export interface CircleEvent {
  event: {
    id: string;
    title: string;
    slug: string;
    eventDate: string;
    startTime: string | null;
    startDateTime: string | null;
    city: string | null;
  };
  goingCount: number;
  /** Members of THIS circle only; a member who hides their attendance is left out. */
  going: Array<{ id: string; name: string; holdsPass: boolean }>;
  /** The caller's own standing, whatever they show others. */
  me: "pass" | "going" | null;
}

export interface CircleInviteLink {
  token: string;
  url: string;
  expiresAt: string;
}

/** Every circle you belong to. Up to ten — the cap is enforced server-side. */
export function listMyCircles() {
  return api.get<ApiResponse<{ circles: Circle[] }>>("/circles");
}

/** An invite the circle sent that is still waiting. Admins only. */
export interface SentCircleInvite {
  id: string;
  expiresAt: string;
  createdAt: string;
  invitee: { id: string; name: string; avatarUrl: string | null };
  invitedBy: { id: string; name: string };
}

/** GET /circles/:id. The circle is `data` itself, not `data.circle`. */
export interface CircleDetail extends Circle {
  pendingInviteCount: number;
  inviteLink: CircleInviteLink | null;
  /** Admins only; null for an ordinary member. */
  sentInvites: SentCircleInvite[] | null;
}

export function createCircle(input: { name: string; emoji?: string }) {
  return api.post<ApiResponse<Circle>>("/circles", input);
}

export function getCircle(circleId: string) {
  return api.get<ApiResponse<CircleDetail>>(`/circles/${circleId}`);
}

export function updateCircle(circleId: string, input: { name?: string; emoji?: string }) {
  return api.patch<ApiResponse<Circle>>(`/circles/${circleId}`, input);
}

/** The inviter or an admin takes back an invite nobody answered. Silent; 409 once answered. */
export function withdrawCircleInvite(circleId: string, inviteId: string) {
  return api.delete<void>(`/circles/${circleId}/invites/${inviteId}`);
}

export function deleteCircle(circleId: string) {
  return api.delete<ApiResponse<{ message: string }>>(`/circles/${circleId}`);
}

/** Invite by phone. Silent decline — the sender is never told it was refused. */
export function inviteToCircle(circleId: string, input: { phone?: string; userId?: string }) {
  return api.post<ApiResponse<{ kind: "in_app" | "link"; invite: { id: string } | null; link: CircleInviteLink | null }>>(
    `/circles/${circleId}/invites`,
    input,
  );
}

/**
 * Mint a shareable join link.
 *
 * No recipient is recorded, on purpose — this is meant to be pasted into a
 * group chat, and a recipient column would quietly turn it into a list of who
 * was asked. Revocable and expiring instead.
 */
export function createCircleInviteLink(circleId: string) {
  return api.post<ApiResponse<CircleInviteLink>>(`/circles/${circleId}/invite-link`, {});
}

export function revokeCircleInviteLink(circleId: string) {
  return api.delete<ApiResponse<{ message: string }>>(`/circles/${circleId}/invite-link`);
}

/** Public preview of a join link — works before the viewer has an account. */
export function previewCircleInvite(token: string) {
  return api.get<ApiResponse<{ circle: Pick<Circle, "id" | "name" | "emoji" | "memberCount"> }>>(
    `/circles/join/${token}`,
  );
}

export function joinCircleByToken(token: string) {
  return api.post<ApiResponse<Circle>>(`/circles/join/${token}`, {});
}

/** Remove somebody, or leave by passing your own id. */
export function removeCircleMember(circleId: string, userId: string) {
  return api.delete<ApiResponse<{ message: string }>>(`/circles/${circleId}/members/${userId}`);
}

export function promoteCircleMember(circleId: string, userId: string) {
  return api.post<ApiResponse<{ message: string }>>(
    `/circles/${circleId}/members/${userId}/promote`,
    {},
  );
}

/** An admin steps down (or is stepped down). The last admin is refused, as leaving is. */
export function demoteCircleMember(circleId: string, userId: string) {
  return api.post<ApiResponse<Circle>>(`/circles/${circleId}/members/${userId}/demote`, {});
}

export function getCircleEvents(circleId: string) {
  return api.get<ApiResponse<{ circleId: string; memberCount: number; events: CircleEvent[] }>>(
    `/circles/${circleId}/events`,
  );
}
